import { DEFAULT_INTELLIGENCE_WINDOW } from '@conversed/shared/src/central/constants/intelligence';
import { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import { AIPerformanceScoresSchema } from '@conversed/shared/src/central/types/ai-performance';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { TimeWindow } from '@conversed/shared/src/central/types/time-window.type';
import { z } from 'zod';
import { AccuracyTestSchema } from '../dashboard/dashboard.schema';
import { DEFAULT_STUDIO_PERFORMANCE_WINDOW } from './home.default';

// AI Performance chart inputs - reused on Home v2 so its chart shows the same data
// as the Dashboard (subjective/objective series gated by the same settings).
export const AIChartSettingsSchema = z.object({
  showSubjective: z.boolean(),
  showCombined: z.boolean(),
  showObjective: z.boolean(),
});

export const AIAccuracyBenchmarkSchema = z.object({
  enabled: z.boolean(),
  value: z.number(),
});

export const HomeDataSchema = z.object({
  botName: z.string(),
  totalQuestions: z.number(),
  aiAccuracy: z.number(),
  activeUsers: z.number(),
});

// Time window for the Intelligence section. Default 7d.
export const TimeWindowSchema = z.enum(TimeWindow);

export const IntelligenceQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
});

export const SentimentBucketsSchema = z.object({
  unhappy: z.number(),
  neutral: z.number(),
  happy: z.number(),
  total: z.number(),
});

export const IntelligenceTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  volume: z.number(),
  lastAnalyzedAt: z.string().nullable(),
  // Weighted satisfaction score 0..1 ((positive + 0.5*neutral) / total); null when no
  // classified conversations exist yet. Drives the sentiment bar length and colour.
  sentimentScore: z.number().nullable(),
  sentiment: SentimentBucketsSchema,
});

// One real question for the busiest topic's hero card, taken from its largest
// sentiment bucket (see resolveDominantSentiment). Null when the source stores no
// question text (Email, Voice) or the topic has no classified questions.
export const IntelligenceHeroQuestionSchema = z.object({
  topicId: z.string(),
  sentiment: z.enum(Sentiment),
  question: z.string(),
});

export const IntelligenceResponseSchema = z.object({
  window: TimeWindowSchema,
  topics: z.array(IntelligenceTopicSchema),
  heroQuestion: IntelligenceHeroQuestionSchema.nullable(),
});

// Keyset paging: pass the previous page's nextCursor back as cursorCreatedAt +
// cursorIds (comma-separated) to get the next page. No cursor = first page.
export const IntelligenceQuestionsQuerySchema = z.object({
  topicId: z.string(),
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
  sentiment: z.enum(Sentiment).optional(),
  cursorCreatedAt: z.string().optional(),
  cursorIds: z.string().optional(),
});

export const IntelligenceQuestionSchema = z.object({
  _id: z.string(),
  question: z.string(),
  answer: z.string(),
  sentiment: z.enum(Sentiment).nullable(),
  isIntentRedirect: z.boolean().optional(),
});

export const IntelligenceQuestionsCursorSchema = z.object({
  createdAt: z.string(),
  ids: z.array(z.string()),
});

export const IntelligenceQuestionsResponseSchema = z.object({
  questions: z.array(IntelligenceQuestionSchema),
  nextCursor: IntelligenceQuestionsCursorSchema.nullable(),
});

export const WhatsChangedQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_INTELLIGENCE_WINDOW),
});

// One day bucket of a topic's volume, for the trend sparkline.
export const TopicTrendPointSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const WhatsChangedTopicSchema = z.object({
  id: z.string(),
  name: z.string(),
  currentVolume: z.number(),
  priorVolume: z.number(),
  currentSentimentScore: z.number().nullable(),
  priorSentimentScore: z.number().nullable(),
  currentSentiment: SentimentBucketsSchema,
  priorSentiment: SentimentBucketsSchema,
  firstSeenAt: z.string().nullable(),
  lastAnalyzedAt: z.string().nullable(),
  trend: z.array(TopicTrendPointSchema),
});

export const WhatsChangedResponseSchema = z.object({
  window: TimeWindowSchema,
  topics: z.array(WhatsChangedTopicSchema),
});

// Snapshot of one studio for the "Other studios" section. `isInstalled` reflects
// whether the company group has that studio activated.
export const StudioSnapshotSchema = z.object({
  type: z.enum(StudioType),
  isInstalled: z.boolean(),
  metric: z.number(),
});

// The Dashboard teaser a company without Intelligence gets: the window's busiest
// hot topic, cut to what the card draws. Nothing else from Intelligence reaches a
// locked company, so no id, no dates and no score.
export const HotTopicTeaserSchema = z.object({
  name: z.string(),
  volume: z.number(),
  unhappy: z.number(),
  neutral: z.number(),
  happy: z.number(),
});

// Studio performance metrics included in the overview. Chat, Ticket and Voice carry
// real data; Email fields are placeholders (0) until email tracking is implemented.
export const StudioPerformanceSchema = z.object({
  // Chat: real data
  questionsAnswered: z.number(),
  uniqueUsers: z.number(),
  // Chat: whether the company has CSAT set up at all - either a manual score
  // override or at least one CSAT entry in Core app_config. No package gate.
  // False hides the card entirely, there is no upsell state.
  csatConfigured: z.boolean(),
  // CSAT score (0..100). Null means no thumbs data in the selected window, so
  // the card shows a dash. Only meaningful while csatConfigured is true.
  csatPercent: z.number().nullable(),
  // Chat: users with exactly one question in the window vs. users with more than
  // one - the "Single questions vs. conversations" card.
  singleQuestions: z.number(),
  groupConversations: z.number(),
  // Chat: whether the company has the Time saved card configured
  // (`timeSavedEnabled` in platform settings). False hides the card.
  timeSavedEnabled: z.boolean(),
  // Ticket: real data
  // Deflected + escalated (conversations that went through the ticket deflection flow).
  responsesGenerated: z.number(),
  // (deflected + noButtonPress) / total * 100. 0 = no data (same sentinel as Dashboard).
  deflectionRate: z.number(),
  // Email
  draftsGenerated: z.number(),
  emailsSent: z.number(),
  uniqueReceivers: z.number(),
  // Voice: real data (voice studios only; 0/null/false for other studios)
  voiceConversationCount: z.number(),
  // Mean call duration. 0 when there are no calls in the window.
  voiceAvgDurationSecs: z.number(),
  voiceTotalDurationSecs: z.number(),
  // Mean LLM response time. Null when no call reported a response time.
  voiceAvgResponseTimeSecs: z.number().nullable(),
  // Whether an ElevenLabs agent is assigned to this company.
  voiceAgentConfigured: z.boolean(),
  // Hot Topics teaser card, only for a company without Intelligence. Null for an
  // Intelligence company, and when the window has no topic with sentiment data.
  hotTopicTeaser: HotTopicTeaserSchema.nullable(),
});

export const StudioPerformanceQuerySchema = z.object({
  window: TimeWindowSchema.default(DEFAULT_STUDIO_PERFORMANCE_WINDOW),
});

export const HomeOverviewSchema = z.object({
  botName: z.string(),
  aiAccuracy: z.number(),
  totalQuestions: z.number(),
  activeUsers: z.number(),
  studios: z.array(StudioSnapshotSchema),
  intelligence: IntelligenceResponseSchema,
  whatsChanged: WhatsChangedResponseSchema,
  studioPerformance: StudioPerformanceSchema,
  // Inputs for the A.I. Performance chart - same data the Dashboard chart uses.
  // Bundled here so the whole Home v2 page loads in one request (no fetch-on-expand).
  performanceScores: AIPerformanceScoresSchema,
  accuracyTests: AccuracyTestSchema.array().default([]),
  aiChartSettings: AIChartSettingsSchema,
  aiAccuracyBenchmark: AIAccuracyBenchmarkSchema,
});
